import React, { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Hooks } from "@xt/xtbase";
const { useTranslation } = Hooks;
import store from "store";
import Storage from "utils/storage";

import XtSvg from "components/xt/svg";
import XtScrollArrow from "components/xt/scroll/arrow";

import MarketTip from "./marketTip";
import LeverStep from "./leverStep";
import CPMT_tradingview_nav from "./tradingview/nav";
import CPMT_tradingview_content from "./tradingview/content";
import CPMT_depth_nav from "./depth/nav";
import CPMT_depth_content from "./depth/content";
import CPMT_intotheblock_nav from "./intotheblock/nav";
import CPMT_intotheblock_content from "./intotheblock/content";

import styles from "./index.module.scss";

import { LayoutH5ActiveKeyEnum } from "store/trade";

enum ChartTypeEnum {
  tradingview = "tradingview", //TradingView 视图
  depth = "depth", //深度 视图
  intotheblock = "intotheblock", //链上数据 视图
}

export interface OptionTradingViewProps {
  interval?: string; //tradingView周期
  chartType?: number; //设置主数据列的样式, 0=Bar,1=Candle,2=Line,3=Area
}
export function getOptionTradingView(option: WithUndefined<OptionTradingViewProps>) {
  return {
    interval: "15",
    chartType: 1,
    ...(option || Storage.get("tvOption") || {}),
  };
}
export interface ActionTradingViewProps {
  actionId?: string; //操作ID
}

export interface OptionDepthProps {
  scope: number;
}

const Main: React.FC = () => {
  const t = useTranslation();
  const { isH5 } = store.app;
  const { layoutH5ActiveKey } = store.trade;

  const [chartType, setChartType] = useState<ChartTypeEnum>(ChartTypeEnum.tradingview);
  useEffect(() => {
    if (isH5 && chartType === ChartTypeEnum.intotheblock) setChartType(ChartTypeEnum.tradingview);
  }, [isH5]);

  const [option_tradingview, setOption_tradingview] = useState<OptionTradingViewProps>();
  const [action_tradingview, setAction_tradingview] = useState<ActionTradingViewProps>();

  const [option_depth, setOption_depth] = useState<OptionDepthProps>({ scope: 0.05 });
  const [option_intotheblock, setOption_intotheblock] = useState();

  const el = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const checkIsFullScreen = useCallback(() => {
    const DOC: any = document;
    return !!(DOC.fullscreenElement || DOC.mozFullScreenElement || DOC.webkitFullscreenElement || DOC.msFullscreenElement);
  }, []);
  const onFullScreen = useCallback(() => {
    const DOC: any = document;
    const $el: any = el.current;
    if (!$el) return;

    if (checkIsFullScreen()) {
      setIsFullScreen(false);
      if (DOC.exitFullscreen) {
        DOC.exitFullscreen();
      } else if (DOC.msExitFullscreen) {
        DOC.msExitFullscreen();
      } else if (DOC.mozCancelFullScreen) {
        DOC.mozCancelFullScreen();
      } else if (DOC.webkitExitFullscreen) {
        DOC.webkitExitFullscreen();
      }
    } else {
      setIsFullScreen(true);
      if ($el.requestFullscreen) {
        $el.requestFullscreen();
      } else if ($el.msRequestFullscreen) {
        $el.msRequestFullscreen();
      } else if ($el.mozRequestFullScreen) {
        $el.mozRequestFullScreen();
      } else if ($el.webkitRequestFullscreen) {
        $el.webkitRequestFullscreen((Element as any).ALLOW_KEYBOARD_INPUT);
      }
    }
  }, []);
  useEffect(() => {
    setIsFullScreen(checkIsFullScreen());
  }, []);

  if (isH5 && layoutH5ActiveKey !== LayoutH5ActiveKeyEnum.chart) return <></>;

  return (
    <div ref={el} className={styles.main}>
      <div className={styles.body}>
        <div className={styles.nav}>
          <XtScrollArrow className={styles.navLeft} resetEffect={chartType}>
            {chartType === ChartTypeEnum.tradingview && (
              <CPMT_tradingview_nav option={option_tradingview} setOption={setOption_tradingview} setAction={setAction_tradingview} />
            )}
            {chartType === ChartTypeEnum.depth && <CPMT_depth_nav option={option_depth} setOption={setOption_depth} />}
            {chartType === ChartTypeEnum.intotheblock && <CPMT_intotheblock_nav option={option_intotheblock} setOption={setOption_intotheblock} />}
          </XtScrollArrow>
          <div className={styles.navRight}>
            <button
              className={cx("btnTxt btnHover", { [styles.navAtv]: chartType === ChartTypeEnum.tradingview })}
              onClick={() => setChartType(ChartTypeEnum.tradingview)}
            >
              {t(isH5 ? "trade.chartBasic" : "trade.tradingView")}
            </button>
            <button className={cx("btnTxt btnHover", { [styles.navAtv]: chartType === ChartTypeEnum.depth })} onClick={() => setChartType(ChartTypeEnum.depth)}>
              {t(isH5 ? "trade.chartDepth" : "trade.depthMap")}
            </button>
            {!isH5 && (
              <>
                <button className={cx("btnTxt btnHover", styles.navZoom)} onClick={onFullScreen}>
                  <XtSvg icon={isFullScreen ? "zoom-out" : "zoom-in"} />
                </button>
              </>
            )}
          </div>
        </div>
        <div className={styles.content}>
          {chartType === ChartTypeEnum.tradingview && <CPMT_tradingview_content option={option_tradingview} action={action_tradingview} />}
          {chartType === ChartTypeEnum.depth && <CPMT_depth_content option={option_depth} />}
          {chartType === ChartTypeEnum.intotheblock && <CPMT_intotheblock_content option={option_intotheblock} />}
        </div>
        <MarketTip />
      </div>
      <LeverStep />
    </div>
  );
};

export default observer(Main);
